import has from 'lodash/has'
import isEmpty from 'lodash/isEmpty'
import isNaN from 'lodash/isNaN'
import isNil from 'lodash/isNil'
import toString from 'lodash/toString'
import moment from 'moment'

import * as PoziUtils from 'api/utils'

// List of accepted rubbish types
export const rubbishTypes = ['gls', 'grn', 'hw', 'rec', 'rub', 'swp']

// Calculate next pickup dates to the garbage GeoJSON dataset.
// http://standards.opencouncildata.org/#/garbage-collection-zones
export default function (flatData) {
  // console.debug('Calculate next pickup dates to the garbage GeoJSON dataset.')

  if (isNil(flatData) || isEmpty(flatData)) {
    return flatData
  }

  fixIncorrectSchema(flatData)

  flatData.rubbish_next_pickup = getNextPickup(flatData, 'rub')
  flatData.recycling_next_pickup = getNextPickup(flatData, 'rec')
  flatData.green_waste_next_pickup = getNextPickup(flatData, 'grn')
  flatData.hard_waste_next_pickup = getNextPickup(flatData, 'hw')
  flatData.glass_waste_next_pickup = getNextPickup(flatData, 'gls')
  flatData.street_sweeping_starting_week = getNextPickup(flatData, 'swp')

  return flatData
}

// Handle incorrect schema from NGSC where they used RUB_WEEK instead of RUB_WEEKS
//  http://www.data.gov.au/dataset/ngsc-garbage-collection-zones-mod/resource/d7b746fe-7e2c-46c1-9fe9-04c8cfd49d8d
// Note: this does not handle case mismatches, so something called `.._WEEK` will not be picked up.
export const fixIncorrectSchema = (flatData) => {
  for (const type of rubbishTypes) {
    const weekField = `${type}_week`
    const weeksField = `${type}_weeks`
    if (has(flatData, weekField) && !has(flatData, weeksField)) {
      flatData[weeksField] = flatData[weekField]
      delete flatData[weekField]
    }
  }
}

/**
 * Finds the next pickup date for a particular collection and returns
 * this as a nicely formatted string
 *
 * @param flatData {object} Object with collection information
 * @param rubbishType {String} Abbreviated name of the type of rubbish collection
 * @returns getNextPickup {String} The date of the next pickup as a pretty string
 *
 */
export const getNextPickup = function (flatData, rubbishType) {
  const startDatePropertyName = `${rubbishType}_start`
  const frequencyWeeksPropertyName = `${rubbishType}_weeks`
  const durationDaysPropertyName = `${rubbishType}_dur`
  const dateListPropertyName = `${rubbishType}_dates`
  const dayPropertyName = `${rubbishType}_day`

  const stringStartDate = toString(PoziUtils.getPropertyValueCaseInsensitive(flatData, startDatePropertyName, '')).trim()
  const frequencyWeeks = getNumberOrNull(PoziUtils.getPropertyValueCaseInsensitive(flatData, frequencyWeeksPropertyName, null)) || 0
  const durationDays = getNumberOrNull(PoziUtils.getPropertyValueCaseInsensitive(flatData, durationDaysPropertyName, null)) || 0
  const dateListString = toString(PoziUtils.getPropertyValueCaseInsensitive(flatData, dateListPropertyName, '')).trim()
  const stringDay = toString(PoziUtils.getPropertyValueCaseInsensitive(flatData, dayPropertyName, '')).trim() || null

  // Create a clean array of date strings without leading or trailing white spaces,
  // nor empty strings
  const dateList = dateListString.split(/\s*,\s*/).filter(item => item !== '')

  const hasDateList = dateList.length > 0

  const todayDate = moment()
  const hasStartDate = !!stringStartDate
  const startDate = hasStartDate ? moment(stringStartDate, 'YYYY-MM-DD') : null
  const hasFrequencyWeeks = !!frequencyWeeks

  // Data must either have a start date or a list of dates
  if (!hasStartDate && !hasDateList) {
    return stringDay
  }

  // Find the next pickup date
  let nextPickupDate = null
  if (hasStartDate && hasFrequencyWeeks) {
    nextPickupDate = calculateNextPickupDate(todayDate, startDate, frequencyWeeks, durationDays)
  } else if (dateList.length > 0) {
    nextPickupDate = findNextPickupDateFromList(todayDate, dateList)
  } else {
    console.log('getNextPickup - Data not available to calculate next pickup date.')
  }

  // console.debug('Garbage getNextPickup - result next-pickup', nextPickupDate)

  return getNextPickupDateAsPrettyString(todayDate, nextPickupDate)
}

/**
 * Finds the next pickup date from a list of dates in string format. It assumes
 * that the date list is in chronological order
 *
 * Note: the 'duration' (or '_dur') functionality is currently not supported by this function!
 *
 * @param todayDate {moment} Today's date in moment format
 * @param dateList {Array<String>} List of dates
 * @returns nextPickupDate {moment} The date of the next pickup
 *
 */
export const findNextPickupDateFromList = function (todayDate, dateList) {
  for (const key in dateList) {
    const dateString = dateList[key]
    if (!dateString) {
      continue
    }
    const pickupDate = moment(dateList[key], 'YYYY-MM-DD')
    if (!pickupDate.isValid()) {
      continue
    }
    if (todayDate.isSameOrBefore(pickupDate, 'day')) {
      return pickupDate
    }
  }

  return null
}

/**
 * Calulates the next pickup date based on a start date and pickup frequency.
 *
 * @param todayDate {moment} Today's date in moment format
 * @param startDate {moment} Start date in moment format
 * @param frequencyWeeks {integer} Frequency of pickup in number of weeks
 * @param durationDays {integer} Duration of validity in number of days
 * @returns nextPickupDate {moment} The date of the next pickup
 *
 */
export const calculateNextPickupDate = function (todayDate, startDate, frequencyWeeks = 0, durationDays = 0) {
  if (!(startDate instanceof moment) || !startDate.isValid()) {
    return null
  }

  // If start date is in the future, we can safely return that straight away
  if (startDate.isSameOrAfter(todayDate, 'day')) {
    return startDate
  }

  // If there are no frequency weeks nor a duration value, we can return null since
  // there is not going to be an upcoming collection
  if (!frequencyWeeks && !durationDays) {
    return null
  }

  // Calculate days to next pickup using the frequency weeks/days
  const frequencyDays = 7 * frequencyWeeks
  let daysDiff = todayDate.diff(startDate, 'days')

  if (frequencyDays) {
    daysDiff = daysDiff % frequencyDays
  }

  // If the number of days are zero, we can safely return today's date
  if (daysDiff === 0) {
    return todayDate
  }

  // If the number of days are within the duration days, we can return
  // today's date minus the days diff
  if (daysDiff < durationDays) {
    const nextPickupDate = todayDate.clone().subtract(daysDiff, 'days')
    return nextPickupDate
  }

  // Now we're here and if the event doesn't repeat, we don't find a next date
  if (frequencyDays === 0) {
    return null
  }

  // Else we calculate and set the next pickup date
  const daysToNextPickup = frequencyDays - daysDiff
  const nextPickupDate = todayDate.clone().add(daysToNextPickup, 'days')

  return nextPickupDate
}

/**
 * Generates the next pickup date as a nicely formatted string showing Today/Tomorrow
 * when applicable.
 *
 * @param todayDate {moment} Today's date in moment format
 * @param nextPickupDate {moment} Next pickup date in moment format
 * @param daysToNextPickup {integer} Number of days till next pickup.
 * @returns returnDateString {string} The date of the next pickup in a nicely formatted string format
 *
 */
export const getNextPickupDateAsPrettyString = function (todayDate, nextPickupDate) {
  if (!(nextPickupDate instanceof moment)) {
    console.warn('getNextPickupDateAsPrettyString - pickup date is not a moment object', nextPickupDate)
    nextPickupDate = null
  }

  if (isNil(nextPickupDate)) {
    return 'None'
  }

  // The following makes sure that if there is less than 24 hours between today and tomorrow,
  // the daysToNextPickup will still be 1.
  // We do this by making sure that both dates have the same starting time
  const startOfNextPickupDate = nextPickupDate.startOf('day')
  const startOfTodayDate = todayDate.startOf('day')
  const daysToNextPickup = startOfNextPickupDate.diff(startOfTodayDate, 'days')

  let nextPickupDateString = nextPickupDate.format('dddd Do MMMM')

  if (daysToNextPickup === 0) {
    nextPickupDateString = 'Today (' + nextPickupDateString + ')'
  } else if (daysToNextPickup === 1) {
    nextPickupDateString = 'Tomorrow (' + nextPickupDateString + ')'
  }

  // console.debug('Garbage getNextPickupDateAsPrettyString', nextPickupDateString)

  return nextPickupDateString
}

/**
 * Tries to convert argument to a number (integer) or else returns null
 *
 * @param value {*} Argument to be converted to an integer
 * @returns {number|null} The integer value of the input or null
 *
 */
export const getNumberOrNull = (value) => {
  const number = parseInt(value, 10)
  return isNaN(number) ? null : number
}
